import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { flushSync } from "react-dom";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import AdminAnalysisPopup from "../../shared/modules/AdminAnalysisPopup";
import { apiFetch } from "../../api/apiClient";

const normalizeSchemaTypes = (schemasGenerated) => {
  if (Array.isArray(schemasGenerated)) {
    return schemasGenerated.map((item) => String(item)).filter(Boolean);
  }
  if (typeof schemasGenerated === "string") {
    return schemasGenerated
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const formatRunTimestamp = (value) => {
  if (!value) return "-";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const dedupeRunsById = (rows) => {
  const seen = new Set();
  return rows.filter((row, idx) => {
    const runId = String(row?.run_id ?? row?.runId ?? idx);
    if (seen.has(runId)) {
      return false;
    }
    seen.add(runId);
    return true;
  });
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildComparisonData = (currentRun, baselineRun) => {
  if (!currentRun) return null;

  const rows = [
    ["material_cost", "Material"],
    ["process_cost", "Process"],
    ["packaging_cost", "Packaging"],
    ["ohp_cost", "OHP"],
    ["total_cost", "Total"],
  ].map(([key, label]) => {
    const baseline = toNumberOrNull(baselineRun?.[key]);
    const current = toNumberOrNull(currentRun?.[key]);
    const delta_pct =
      baseline !== null && baseline !== 0 && current !== null
        ? ((current - baseline) / baseline) * 100
        : null;

    return {
      key,
      label,
      baseline,
      current,
      delta_pct,
    };
  });

  return {
    current_run_id: currentRun?.run_id ?? currentRun?.runId ?? null,
    baseline_run_id: baselineRun?.run_id ?? baselineRun?.runId ?? null,
    tower: currentRun?.tower ?? "",
    commodity: currentRun?.commodity ?? "",
    rows,
  };
};

export default function TracePage() {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    surface: "#1e1f20",
    border: "rgba(255,255,255,0.12)",
    textPrimary: "#F1F2F3",
    textSecondary: "#A0A8AB",
  };

  const [pickerOpen, setPickerOpen] = useState(false);
  const [runOptions, setRunOptions] = useState([]);
  const [runOptionsLoading, setRunOptionsLoading] = useState(false);
  const [runOptionsError, setRunOptionsError] = useState("");
  const [runSearch, setRunSearch] = useState("");
  const [towerFilter, setTowerFilter] = useState("");
  const [commodityFilter, setCommodityFilter] = useState("");
  const [candidateRunId, setCandidateRunId] = useState(null);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [expandedRunIds, setExpandedRunIds] = useState({});

  const [activeRunId, setActiveRunId] = useState("");
  const [activeRunMeta, setActiveRunMeta] = useState(null);
  const [loadingRun, setLoadingRun] = useState(false);
  const [loadingRunId, setLoadingRunId] = useState("");
  const [statusMessage, setStatusMessage] = useState(
    "Select a run from the left panel or use Add Run to load payload from aipc_run_log.",
  );
  const [pageError, setPageError] = useState("");
  const latestRequestedRunIdRef = useRef("");
  const pendingRunLoadTimerRef = useRef(null);

  const [schemaTypes, setSchemaTypes] = useState([]);
  const [schemaDefinitionColumns, setSchemaDefinitionColumns] = useState([]);
  const [schemaDefinitions, setSchemaDefinitions] = useState([]);
  const [derivedSchemaDefinitionColumns, setDerivedSchemaDefinitionColumns] = useState([]);
  const [derivedSchemaDefinitions, setDerivedSchemaDefinitions] = useState([]);
  const [frontendReturnItems, setFrontendReturnItems] = useState([]);
  const [requestPayload, setRequestPayload] = useState(null);
  const [derivedConstraints, setDerivedConstraints] = useState(null);
  const [derivedPrompts, setDerivedPrompts] = useState(null);
  const [outputSchemaPrompts, setOutputSchemaPrompts] = useState(null);
  const [costModelResults, setCostModelResults] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [traceSqlScript, setTraceSqlScript] = useState("");
  const [sqlCopyStatus, setSqlCopyStatus] = useState("idle");
  const traceSqlKey = searchParams.get("sqlKey") || "";

  const fetchRunOptions = async () => {
    setRunOptionsLoading(true);
    setRunOptionsError("");
    try {
      const response = await apiFetch("/api/analysis/run-ids?limit=500");
      const data = await response.json();
      if (!response.ok || data.status !== "success") {
        throw new Error(data.error || "Failed to load run list");
      }
      setRunOptions(Array.isArray(data.runs) ? data.runs : []);
    } catch (error) {
      setRunOptionsError(error?.message || "Failed to load run list.");
    } finally {
      setRunOptionsLoading(false);
    }
  };

  const openPicker = () => {
    setPickerOpen(true);
    if (runOptions.length === 0 && !runOptionsLoading) {
      fetchRunOptions();
    }
  };

  const closePicker = () => {
    setPickerOpen(false);
  };

  const towerOptions = useMemo(() => {
    const values = new Set();
    runOptions.forEach((row) => {
      if (row.tower) values.add(String(row.tower));
    });
    return Array.from(values).sort();
  }, [runOptions]);

  const commodityOptions = useMemo(() => {
    const values = new Set();
    runOptions.forEach((row) => {
      if (towerFilter) {
        if (String(row.tower || "").toLowerCase() === towerFilter && row.commodity) {
          values.add(String(row.commodity));
        }
      } else if (row.commodity) {
        values.add(String(row.commodity));
      }
    });
    return Array.from(values).sort();
  }, [runOptions, towerFilter]);

  const filteredRuns = useMemo(() => {
    const q = runSearch.trim().toLowerCase();
    let rows = runOptions;
    if (towerFilter) {
      rows = rows.filter((row) => String(row.tower || "").toLowerCase() === towerFilter);
    }
    if (commodityFilter) {
      rows = rows.filter((row) => String(row.commodity || "").toLowerCase() === commodityFilter);
    }
    if (!q) return rows;
    return rows.filter((row) => {
      const blob = [
        row.run_id,
        row.tower,
        row.commodity,
        row.response_status,
        row.productName,
        row.country,
        row.skuWeight,
        row.marketPositioning,
        row.annualVolume,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [runOptions, runSearch, towerFilter, commodityFilter]);

  const visibleRuns = useMemo(() => dedupeRunsById(filteredRuns), [filteredRuns]);

  const selectedRunForComparison = useMemo(() => {
    const selectedRunId = candidateRunId || activeRunId;
    if (!selectedRunId) return null;
    return (
      runOptions.find((row) => String(row?.run_id ?? row?.runId ?? "") === String(selectedRunId)) || null
    );
  }, [runOptions, candidateRunId, activeRunId]);

  const fallbackComparisonData = useMemo(() => {
    const currentRun = activeRunMeta || selectedRunForComparison;
    if (!currentRun) return null;

    const tower = String(currentRun?.tower || "").trim().toLowerCase();
    const commodity = String(currentRun?.commodity || "").trim().toLowerCase();
    if (!tower || !commodity) return null;

    const baselineRun = [...runOptions]
      .filter((row) => {
        const rowTower = String(row?.tower || "").trim().toLowerCase();
        const rowCommodity = String(row?.commodity || "").trim().toLowerCase();
        return Boolean(row?.baseline) && rowTower === tower && rowCommodity === commodity;
      })
      .sort((a, b) => Number(b?.run_id ?? b?.runId ?? 0) - Number(a?.run_id ?? a?.runId ?? 0))[0];

    return buildComparisonData(currentRun, baselineRun);
  }, [activeRunMeta, selectedRunForComparison, runOptions]);

  const fetchComparisonByRunId = async (runId) => {
    if (!runId) {
      setComparisonData(null);
      return;
    }

    try {
      const response = await apiFetch(
        `/api/analysis/run-log/${encodeURIComponent(String(runId))}/comparison`,
      );
      const data = await response.json();
      if (!response.ok || data.status !== "success") {
        throw new Error(data.error || "Failed to load comparison");
      }

      if (latestRequestedRunIdRef.current !== String(runId)) {
        return;
      }

      setComparisonData(data.comparison ?? null);
    } catch (_error) {
      // Keep fallback comparison if the dedicated endpoint is unavailable.
    }
  };

  const fetchLatestBaselineForRun = async (currentRun) => {
    const tower = String(currentRun?.tower || "").trim().toLowerCase();
    const commodity = String(currentRun?.commodity || "").trim().toLowerCase();
    if (!tower || !commodity) {
      return null;
    }

    try {
      const response = await apiFetch("/api/analysis/run-ids?limit=1000&baseline_only=true");
      const data = await response.json();
      if (!response.ok || data.status !== "success") {
        throw new Error(data.error || "Failed to load baseline runs");
      }

      const baselineRun = (Array.isArray(data.runs) ? data.runs : [])
        .filter((row) => {
          const rowTower = String(row?.tower || "").trim().toLowerCase();
          const rowCommodity = String(row?.commodity || "").trim().toLowerCase();
          const rowRunId = String(row?.run_id ?? row?.runId ?? "");
          const currentRunId = String(currentRun?.run_id ?? currentRun?.runId ?? "");
          return rowTower === tower && rowCommodity === commodity && rowRunId !== currentRunId;
        })
        .sort((a, b) => Number(b?.run_id ?? b?.runId ?? 0) - Number(a?.run_id ?? a?.runId ?? 0))[0];

      return baselineRun || null;
    } catch (_error) {
      return null;
    }
  };

  const loadRunById = async (runId) => {
    if (!runId) return false;

    const normalizedRunId = String(runId);
    latestRequestedRunIdRef.current = normalizedRunId;
    setLoadingRun(true);
    setLoadingRunId(normalizedRunId);
    setPageError("");
    setComparisonData(null);

    try {
      void fetchComparisonByRunId(normalizedRunId);

      const response = await apiFetch(`/api/analysis/run-log/${encodeURIComponent(normalizedRunId)}`);
      const data = await response.json();
      if (!response.ok || data.status !== "success") {
        throw new Error(data.error || "Failed to load run details");
      }

      if (latestRequestedRunIdRef.current !== normalizedRunId) {
        return false;
      }

      const run = data.run || {};
      const payload = run.request_payload;
      const parsedPayload =
        payload && typeof payload === "object"
          ? payload
          : payload
            ? { raw_payload: String(payload) }
            : null;

      setActiveRunId(String(run.run_id ?? normalizedRunId));
      setActiveRunMeta(run);
      setRequestPayload(parsedPayload);
      setDerivedConstraints(run.derived_constraints ?? null);
      setDerivedPrompts(run.derived_prompts ?? null);
      setOutputSchemaPrompts(run.output_schema_prompts ?? null);
      setCostModelResults(run.cost_model_results ?? null);
      const baselineRun = await fetchLatestBaselineForRun(run);
      setComparisonData((prev) => {
        if (data.comparison?.baseline_run_id) {
          return data.comparison;
        }
        if (baselineRun) {
          return buildComparisonData(run, baselineRun);
        }
        if (prev?.baseline_run_id) {
          return prev;
        }
        return buildComparisonData(run, null);
      });
      setSchemaTypes(normalizeSchemaTypes(run.schemas_generated));
      setSchemaDefinitionColumns([]);
      setSchemaDefinitions([]);
      setDerivedSchemaDefinitionColumns([]);
      setDerivedSchemaDefinitions([]);
      setFrontendReturnItems([]);
      setStatusMessage(`Payload loaded from run ${run.run_id ?? normalizedRunId}.`);
      return true;
    } catch (error) {
      if (latestRequestedRunIdRef.current === normalizedRunId) {
        setPageError(error?.message || "Failed to load selected run.");
        setStatusMessage("Select a valid run to load payload.");
      }
      return false;
    } finally {
      if (latestRequestedRunIdRef.current === normalizedRunId) {
        setLoadingRun(false);
        setLoadingRunId("");
      }
    }
  };

  const applySelectedRun = async () => {
    if (!candidateRunId) {
      setRunOptionsError("Select one run to continue.");
      return;
    }
    const loaded = await scheduleRunLoad(candidateRunId);
    if (loaded) {
      setPickerOpen(false);
    }
  };

  const refreshActiveRun = async () => {
    if (!activeRunId) {
      await fetchRunOptions();
      return;
    }
    await loadRunById(activeRunId);
  };

  const handleCopySqlScript = async () => {
    if (!traceSqlScript) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(traceSqlScript);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = traceSqlScript;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setSqlCopyStatus("copied");
      setTimeout(() => setSqlCopyStatus("idle"), 1800);
    } catch (error) {
      console.error("Failed to copy SQL script:", error);
    }
  };

  const primeRunSelection = (runId) => {
    const normalizedRunId = String(runId);
    flushSync(() => {
      setCandidateRunId(normalizedRunId);
      setLoadingRunId(normalizedRunId);
      setPageError("");
    });
  };

  const scheduleRunLoad = (runId) =>
    new Promise((resolve) => {
      const normalizedRunId = String(runId);
      if (pendingRunLoadTimerRef.current) {
        clearTimeout(pendingRunLoadTimerRef.current);
      }

      primeRunSelection(normalizedRunId);

      pendingRunLoadTimerRef.current = setTimeout(async () => {
        pendingRunLoadTimerRef.current = null;
        resolve(await loadRunById(normalizedRunId));
      }, 0);
    });

  const handleSidebarRunSelect = (runId) => {
    const normalizedRunId = String(runId);
    void scheduleRunLoad(normalizedRunId);
  };

  const toggleRunExpanded = (runId) => {
    setExpandedRunIds((prev) => ({
      ...prev,
      [runId]: !prev[runId],
    }));
  };

  useEffect(() => {
    fetchRunOptions();
  }, []);

  useEffect(
    () => () => {
      if (pendingRunLoadTimerRef.current) {
        clearTimeout(pendingRunLoadTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!traceSqlKey) {
      setTraceSqlScript("");
      setSqlCopyStatus("idle");
      return;
    }

    try {
      setTraceSqlScript(sessionStorage.getItem(traceSqlKey) || "");
    } catch (error) {
      console.error("Failed to read SQL script for trace page:", error);
      setTraceSqlScript("");
    }
    setSqlCopyStatus("idle");
  }, [traceSqlKey]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          p: 0,
          bgcolor: perlColors.background,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", lg: leftPanelCollapsed ? 72 : 360 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            bgcolor: perlColors.surface,
            borderRight: { xs: "none", lg: `1px solid ${perlColors.border}` },
            borderBottom: { xs: `1px solid ${perlColors.border}`, lg: "none" },
            minHeight: { xs: leftPanelCollapsed ? 72 : 280, lg: 0 },
            maxHeight: { xs: leftPanelCollapsed ? 72 : "42vh", lg: "100%" },
            transition: "width 0.2s ease, max-height 0.2s ease",
          }}
        >
          <Box
            sx={{
              px: leftPanelCollapsed ? 1 : 2,
              py: 1.5,
              borderBottom: `1px solid ${perlColors.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            {!leftPanelCollapsed && (
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: perlColors.textPrimary }}>Projects</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: perlColors.textSecondary }}>
                  {visibleRuns.length} of {runOptions.length} runs
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: leftPanelCollapsed ? 0 : "auto",
              }}
            >
              <Tooltip title="Refresh List">
                <span>
                  <IconButton
                    size="small"
                    onClick={fetchRunOptions}
                    disabled={runOptionsLoading}
                    sx={{
                      color: perlColors.textPrimary,
                      "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={leftPanelCollapsed ? "Expand Projects" : "Collapse Projects"}>
                <IconButton
                  size="small"
                  onClick={() => setLeftPanelCollapsed((prev) => !prev)}
                  sx={{
                    color: perlColors.textPrimary,
                    "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
                  }}
                >
                  {leftPanelCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {!leftPanelCollapsed && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${perlColors.border}`,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search projects..."
                value={runSearch}
                onChange={(e) => setRunSearch(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: perlColors.textPrimary,
                    "& fieldset": { borderColor: perlColors.border },
                    "&:hover fieldset": { borderColor: "#ff6034" },
                    "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                  },
                }}
              />

              <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: perlColors.textPrimary, "&.Mui-focused": { color: "#ff6034" } }}>
                  Tower
                </InputLabel>
                <Select
                  value={towerFilter}
                  onChange={(e) => {
                    setTowerFilter(e.target.value);
                    setCommodityFilter("");
                  }}
                  label="Tower"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: perlColors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    ".MuiSvgIcon-root": { color: perlColors.textPrimary },
                  }}
                >
                  <MenuItem value="">All Towers</MenuItem>
                  {towerOptions.map((tower) => (
                    <MenuItem key={tower} value={tower.toLowerCase()}>
                      {tower}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel sx={{ color: perlColors.textPrimary, "&.Mui-focused": { color: "#ff6034" } }}>
                  Commodity
                </InputLabel>
                <Select
                  value={commodityFilter}
                  onChange={(e) => setCommodityFilter(e.target.value)}
                  label="Commodity"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: perlColors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    ".MuiSvgIcon-root": { color: perlColors.textPrimary },
                  }}
                >
                  <MenuItem value="">All Commodities</MenuItem>
                  {commodityOptions.map((commodity) => (
                    <MenuItem key={commodity} value={commodity.toLowerCase()}>
                      {commodity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {!leftPanelCollapsed && runOptionsError && (
            <Alert severity="error" sx={{ m: 2, mb: 0 }}>
              {runOptionsError}
            </Alert>
          )}

          {!leftPanelCollapsed && (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1.5 }}>
            {runOptionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : visibleRuns.length === 0 ? (
              <Box
                sx={{
                  border: `1px dashed ${perlColors.border}`,
                  borderRadius: "10px",
                  p: 2,
                  textAlign: "center",
                  color: perlColors.textSecondary,
                }}
              >
                No projects match the current filters.
              </Box>
            ) : (
              visibleRuns.map((row, idx) => {
                const runId = String(row.run_id ?? row.runId ?? idx);
                const isSelected = candidateRunId === runId;
                const isExpanded = Boolean(expandedRunIds[runId]);
                const isLoadingCard = loadingRunId === runId;

                return (
                  <Box
                    key={runId}
                    onPointerDown={(event) => {
                      if (event.button !== undefined && event.button !== 0) return;
                      primeRunSelection(runId);
                    }}
                    onClick={() => handleSidebarRunSelect(runId)}
                    sx={{
                      border: `1px solid ${isSelected ? "#ff6034" : perlColors.border}`,
                      borderRadius: "10px",
                      p: 1,
                      mb: 1,
                      cursor: "pointer",
                      bgcolor: isSelected ? "rgba(255, 96, 52, 0.12)" : "transparent",
                      "&:hover": {
                        borderColor: isSelected ? "#ff6034" : "rgba(255,255,255,0.24)",
                        bgcolor: isSelected ? "rgba(255, 96, 52, 0.12)" : "rgba(255,255,255,0.03)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: perlColors.textPrimary }}>
                        Run {runId}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {isLoadingCard && <CircularProgress size={14} sx={{ color: "#ff6034" }} />}
                        <Tooltip title={isExpanded ? "Show less" : "Show more"}>
                          <IconButton
                            size="small"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleRunExpanded(runId);
                            }}
                            sx={{
                              color: perlColors.textSecondary,
                              p: 0.25,
                              "&:hover": { color: "#ff6034", bgcolor: "rgba(255, 96, 52, 0.12)" },
                            }}
                          >
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <Typography
                      sx={{
                        fontSize: "0.74rem",
                        color: perlColors.textPrimary,
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.productName || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: perlColors.textSecondary }}>
                      {row.tower || "-"} | {row.commodity || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: perlColors.textSecondary }}>
                      Run Time: {formatRunTimestamp(row.run_timestamp)}
                    </Typography>

                    {isExpanded && (
                      <Box
                        sx={{
                          mt: 0.75,
                          pt: 0.75,
                          borderTop: `1px solid ${perlColors.border}`,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.35,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.7rem", color: perlColors.textSecondary }}>
                          Status: {row.response_status || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: perlColors.textSecondary }}>
                          User: {row.User || row.user || row.user_email || row.userEmail || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: perlColors.textSecondary }}>
                          {row.country || "-"} | {row.skuWeight || "-"} | {row.marketPositioning || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: perlColors.textSecondary }}>
                          {row.annualVolume || "-"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })
            )}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}>
          {pageError && (
            <Alert severity="error" sx={{ m: 1 }}>
              {pageError}
            </Alert>
          )}
          <Box sx={{ height: "100%" }}>
            <AdminAnalysisPopup
              open={true}
              onClose={() => {}}
              inline={true}
              title="Trace"
              onAddRun={openPicker}
              onRefresh={refreshActiveRun}
              onCopySqlScript={handleCopySqlScript}
              copySqlScriptDisabled={!traceSqlScript}
              copySqlScriptTooltip={
                sqlCopyStatus === "copied" ? "SQL script copied" : "Copy SQL script"
              }
              toolbarInfo={
                activeRunMeta
                  ? `Run ${activeRunMeta.run_id} | ${activeRunMeta.tower || "-"} | ${activeRunMeta.commodity || "-"}`
                  : "No run selected"
              }
              schemaTypes={schemaTypes}
              schemaDefinitionColumns={schemaDefinitionColumns}
              schemaDefinitions={schemaDefinitions}
              derivedSchemaDefinitionColumns={derivedSchemaDefinitionColumns}
              derivedSchemaDefinitions={derivedSchemaDefinitions}
              frontendReturnItems={frontendReturnItems}
              statusMessage={statusMessage}
              requestPayload={requestPayload}
              derivedConstraints={derivedConstraints}
              derivedPrompts={derivedPrompts}
              outputSchemaPrompts={outputSchemaPrompts}
              costModelResults={costModelResults}
              comparisonData={comparisonData || fallbackComparisonData}
              perlColors={perlColors}
            />
          </Box>
        </Box>

        <Dialog
          open={pickerOpen}
          onClose={closePicker}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: perlColors.background,
              color: perlColors.textPrimary,
              border: `1px solid ${perlColors.border}`,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Add Projects</Typography>
            <Tooltip title="Refresh List">
              <IconButton
                size="small"
                onClick={fetchRunOptions}
                sx={{
                  color: perlColors.textPrimary,
                  "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)", color: "#ff6034" },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by run id, tower, commodity, status, or question fields..."
              value={runSearch}
              onChange={(e) => setRunSearch(e.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  color: perlColors.textPrimary,
                  "& fieldset": { borderColor: perlColors.border },
                  "&:hover fieldset": { borderColor: "#ff6034" },
                  "&.Mui-focused fieldset": { borderColor: "#ff6034" },
                },
              }}
            />

            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 200, flex: "1 1 200px" }}>
                <InputLabel sx={{ color: perlColors.textPrimary, "&.Mui-focused": { color: "#ff6034" } }}>
                  Tower
                </InputLabel>
                <Select
                  value={towerFilter}
                  onChange={(e) => {
                    setTowerFilter(e.target.value);
                    setCommodityFilter("");
                  }}
                  label="Tower"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: perlColors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    ".MuiSvgIcon-root": { color: perlColors.textPrimary },
                  }}
                >
                  <MenuItem value="">All Towers</MenuItem>
                  {towerOptions.map((tower) => (
                    <MenuItem key={tower} value={tower.toLowerCase()}>
                      {tower}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 220, flex: "1 1 220px" }}>
                <InputLabel sx={{ color: perlColors.textPrimary, "&.Mui-focused": { color: "#ff6034" } }}>
                  Commodity
                </InputLabel>
                <Select
                  value={commodityFilter}
                  onChange={(e) => setCommodityFilter(e.target.value)}
                  label="Commodity"
                  sx={{
                    color: perlColors.textPrimary,
                    ".MuiOutlinedInput-notchedOutline": { borderColor: perlColors.border },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#ff6034" },
                    ".MuiSvgIcon-root": { color: perlColors.textPrimary },
                  }}
                >
                  <MenuItem value="">All Commodities</MenuItem>
                  {commodityOptions.map((commodity) => (
                    <MenuItem key={commodity} value={commodity.toLowerCase()}>
                      {commodity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {runOptionsLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {runOptionsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {runOptionsError}
              </Alert>
            )}

            {!runOptionsLoading && !runOptionsError && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 1,
                  maxHeight: "52vh",
                  overflow: "auto",
                  pr: 0.5,
                }}
              >
                {visibleRuns.map((row, idx) => {
                  const runId = String(row.run_id ?? row.runId ?? idx);
                  const isSelected = candidateRunId === runId;
                  return (
                    <Box
                      key={runId}
                      onClick={() => setCandidateRunId(runId)}
                      sx={{
                        border: `1px solid ${perlColors.border}`,
                        borderRadius: "8px",
                        p: 1,
                        cursor: "pointer",
                        bgcolor: isSelected ? "rgba(255, 96, 52, 0.12)" : "transparent",
                        "&:hover": {
                          borderColor: isSelected ? "#ff6034" : "rgba(255,255,255,0.24)",
                          bgcolor: isSelected ? "rgba(255, 96, 52, 0.12)" : "rgba(255,255,255,0.03)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                          Run {runId}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => setCandidateRunId(runId)}
                          sx={{ color: "#9aa1a6", "&.Mui-checked": { color: "#ff6034" } }}
                        />
                      </Box>

                      <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                        {row.tower || "-"} | {row.commodity || "-"} | {row.response_status || "-"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                        Run Time: {formatRunTimestamp(row.run_timestamp)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                        User: {row.User || row.user || row.user_email || row.userEmail || "-"}
                      </Typography>

                      <Box sx={{ mt: 0.5 }}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9aa1a6" }}>
                          Questions:
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem" }}>
                          {row.productName || "-"} | {row.country || "-"} | {row.skuWeight || "-"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem" }}>
                          {row.marketPositioning || "-"} | {row.annualVolume || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={closePicker}
              sx={{ color: perlColors.textPrimary, "&:hover": { bgcolor: "rgba(255, 96, 52, 0.12)" } }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={applySelectedRun}
              disabled={!candidateRunId || loadingRun}
              sx={{
                bgcolor: "#ff6034",
                color: "#fff",
                textTransform: "none",
                "&:hover": { bgcolor: "#e55530" },
              }}
            >
              Add Selected
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
}
